import React, { useEffect, useRef, useState } from "react";
import * as APPCONFIG from "../config/app-config.js";
import useRazorpay from "react-razorpay";
import MyYuukke from "../api/MyYuukke";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ProfileService from "../api/ProfileService";

const Payment = ({
  email = "notgiven@gmail.com",
  contactNumber = "",
  serviceId = null,
  name = "Other User",
  userId = null,
  amount = 100,
  type = "un_known_premium",
  subscriptionId = null,
  updatePaymentStatus,
  initId = null,
  uId = null,
  selectedPlan = null,
  autoRenew = true,
}) => {
  const [Razorpay] = useRazorpay();
  const [trackingId, setTrackingId] = useState("");

  const isSubscription = autoRenew && Boolean(selectedPlan?.razorpayPlanId);

  const initialized = useRef(false);
  const noResponse = useRef(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initiatePaymentFlow();
    }
    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (!trackingId || !noResponse.current || isSubscription) return;

    intervalRef.current = setInterval(() => {
      fallbackPaymentCheck();
    }, 2500);

    return () => clearInterval(intervalRef.current);
  }, [trackingId, isSubscription]);

  const fallbackPaymentCheck = async () => {
    try {
      if (!trackingId) return;
      const res = await ProfileService.checkRazorPayFallBack({ orderId: trackingId });

      if (res.data.status === 1) {
        const payments = res.data.data.payments || [];
        const captured = payments.find((p) => p.status === "captured");
        const failed = !captured ? payments.find((p) => p.status === "failed") : null;

        if (captured) {
          noResponse.current = false;
          clearInterval(intervalRef.current);

          savePaymentInfo({
            razorpay_payment_id: captured.id,
            razorpay_order_id: res.data.data.order_id,
            razorpay_signature: `${res.data.data.order_id}|${captured.id}`,
            status: "success",
            email: captured.email,
          });
          return;
        }

        if (failed) {
          noResponse.current = false;
          clearInterval(intervalRef.current);
          updateErrorStatus("error", "PAYMENT-FAILED");
          updatePaymentStatus("failed");
          return;
        }
      }
    } catch (err) {
      console.error("fallbackPaymentCheck error:", err);
    }
  };

  const updateErrorStatus = async (status, message) => {
    try {
      const activeId = sessionStorage.getItem("active_pay_id");
      if (!activeId) return;

      await MyYuukke.updateErrorStatus({
        orderId: activeId,
        amount,
        type,
        subscriptionId: subscriptionId || null,
        email,
        status,
        errorStatus: message,
      });

      sessionStorage.removeItem("active_pay_id");
    } catch (err) {
      console.error("updateErrorStatus failed:", err);
    }
  };

  // Create-only — safe to call every time a payment succeeds here.
  // Backend no-ops if the user's CalendarEventNames row already exists.
  const ensureCalendarDetails = async (resolvedUserId) => {
    if (!resolvedUserId) return;
    try {
      await MyYuukke.createCalendarDetails(resolvedUserId);
    } catch (err) {
      console.error("createCalendarDetails failed:", err);
      // Don't block the success path on this — payment already recorded.
    }
  };

  const savePaymentInfo = async (response) => {
    try {
      const data = {
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_subscription_id || response.razorpay_order_id,
        razorpaySignature: response.razorpay_signature || null,
        amount,
        type,
        subscriptionId: subscriptionId || null,
        email: email || response.email,
        serviceId,
        name,
        realAmount: amount,
        razorpayAmount: amount * 100,
        status: response.status || "success",
      };

      if (userId) data.userId = userId;
      else if (uId) data.userId = uId;

      const apiCall = userId
        ? MyYuukke.savePaymentInforMation(data)
        : MyYuukke.savePaymentInforMationWithoutAuth(data);

      const res = await apiCall;

      const resolvedUserId = userId || uId || res.data?.data?.userId;
      await ensureCalendarDetails(resolvedUserId);

      updatePaymentStatus("success", res.data?.data?.id);
      sessionStorage.removeItem("active_pay_id");
      clearInterval(intervalRef.current);
    } catch (err) {
      console.error("savePaymentInfo error:", err);
      Swal.fire({
        icon: "error",
        title: "Payment recording failed",
        text: "If amount was debited, please contact support: +91 04446314646",
      });
      updatePaymentStatus("dismiss");
      updateErrorStatus("error", "SAVE-FAILED");
      clearInterval(intervalRef.current);
    }
  };

  const initiatePaymentFlow = () => {
    if (isSubscription) {
      const periodText = selectedPlan.subscribeType === "YEARLY" ? "year" : "month";

      Swal.fire({
        title: "Auto-Renewal Setup",
        html: `You are about to set up a subscription for <strong>${selectedPlan.subscriptionName}</strong>.<br><br>
               By proceeding, you agree to an automatic debit of <strong>₹${amount}</strong> every ${periodText}. 
               <br><br><span style="font-size: 0.9em; color: #666;">You will authorize this mandate securely via Razorpay in the next step.</span>`,
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#7a0024",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, I agree",
        cancelButtonText: "Cancel",
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          handlePayment();
        } else {
          updatePaymentStatus("dismiss");
        }
      });
    } else {
      handlePayment();
    }
  };

  const handlePayment = async () => {
    try {
      const key = APPCONFIG.razorPayKey;

      let options = {
        key,
        name: "Yuukke Global Venture",
        description: selectedPlan?.subscriptionName || "Service Subscription",
        image: "https://corporate.yuukke.com/bcav2/auth/image/be6f008b-2fb9-4027-9634-642b614c3607",
        handler: (response) => {
          if (noResponse.current) {
            noResponse.current = false;
            clearInterval(intervalRef.current);
            savePaymentInfo(response);
          }
        },
        prefill: {
          name,
          email,
          contact: contactNumber,
          method: "upi",
        },
        notes: { address: "Yuukke Global Venture" },
        theme: { color: "#a00030" },
        modal: {
          ondismiss: () => {
            updateErrorStatus("error", "USER-CLOSED");
            updatePaymentStatus("dismiss");
            clearInterval(intervalRef.current);
          },
        },
      };

      if (isSubscription) {
        if (!selectedPlan?.razorpayPlanId) {
          toast.error("Subscription Plan ID missing. Please contact support.");
          updatePaymentStatus("dismiss");
          return;
        }

        const formValues = new FormData();
        formValues.append("razorpayPlanId", selectedPlan.razorpayPlanId);
        formValues.append("totalBillingCycles", selectedPlan.subscribeType === "YEARLY" ? 5 : 12);

        if (email) formValues.append("emailId", email);
        if (userId || uId) formValues.append("userId", userId || uId);

        const subRes = await ProfileService.createRazorpaySubscription(formValues);

        if (subRes.data.status !== 1) {
          throw new Error(subRes.data.message || "Failed to initialize subscription");
        }

        const subId = subRes.data.subscriptionId;

        options.subscription_id = subId;
        setTrackingId(subId);
        sessionStorage.setItem("active_pay_id", subId);
      } else {
        const body = {
          emailId: email,
          userId,
          amount: amount * 100,
          realAmount: amount,
          type,
          serviceId,
          ...(initId && { initId }),
          ...(subscriptionId && { subscriptionId }),
        };

        const orderResponse = await ProfileService.getOrderInIt(body);
        if (orderResponse.data.message !== "success") {
          throw new Error("Order init failed");
        }

        const { id: order_id, currency } = orderResponse.data.data;

        options.order_id = order_id;
        options.amount = (amount * 100).toString();
        options.currency = currency;
        setTrackingId(order_id);
        sessionStorage.setItem("active_pay_id", order_id);
      }

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", () => {
        updateErrorStatus("error", "PAYMENT-FAILED");
        clearInterval(intervalRef.current);
      });

      rzp.open();
    } catch (err) {
      console.error("handlePayment error:", err);
      toast.error(err.message || "Something went wrong. Please try again!");
      updatePaymentStatus("dismiss");
      clearInterval(intervalRef.current);
    }
  };

  return <></>;
};

export default Payment;